import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTranslateDynamicFields } from "../../api/queries/useTranslateDynamicFields";
import { countries } from "countries-list";
import { sectors } from "./Sectors";
import {
  COMPANY_FORM_UI_TEXT,
  CompanyFormUiKey,
  INVESTOR_FORM_UI_TEXT,
  InvestorFormUiKey,
  PROFILE_SHELL_UI_TEXT,
  ProfileShellUiKey,
} from "./muslimInvestFormUiText";

const SORTED_SECTORS = [...sectors].sort();

const INVESTMENT_SIZE_KEYS = [
  "ticket1k10k",
  "ticket10k50k",
  "ticket50k100k",
  "ticket100k500k",
  "ticket500k1m",
  "ticket1m5m",
  "ticket5mPlus",
] as const;

const INVESTMENT_SIZE_VALUES = [
  "£1k-10k",
  "£10k-£50k",
  "£50k-£100k",
  "£100k-£500k",
  "£500k-£1M",
  "£1M-£5M",
  "£5M+",
] as const;

const COUNTRY_NAMES = Object.values(countries)
  .map((c) => c.name)
  .sort((a, b) => a.localeCompare(b));

export function useInvestorFormLabels() {
  const { i18n } = useTranslation();
  const uiText = useMemo(() => ({ ...INVESTOR_FORM_UI_TEXT }), []);
  const { data: translatedUiText } = useTranslateDynamicFields(
    uiText,
    i18n.resolvedLanguage || i18n.language,
  );
  const tt = useCallback(
    (key: InvestorFormUiKey) => translatedUiText?.[key] ?? uiText[key],
    [translatedUiText, uiText],
  );

  const sectorUiText = useMemo(
    () =>
      Object.fromEntries(
        SORTED_SECTORS.map((s, i) => [`sector_${i}`, s]),
      ),
    [],
  );
  const { data: translatedSectors } = useTranslateDynamicFields(
    sectorUiText,
    i18n.resolvedLanguage || i18n.language,
  );
  const sectorOptions = useMemo(
    () =>
      SORTED_SECTORS.map((s, i) => ({
        value: s,
        label: translatedSectors?.[`sector_${i}`] ?? s,
      })),
    [translatedSectors],
  );

  const yesNoOptions = useMemo(
    () => [
      { value: "", label: tt("selectOption") },
      { value: "1", label: tt("yes") },
      { value: "0", label: tt("no") },
    ],
    [tt],
  );

  const investmentTypeOptions = useMemo(
    () => [
      { value: "", label: tt("pleaseSelect") },
      { value: "Individual Investor", label: tt("individualInvestor") },
      { value: "Investment Business", label: tt("investmentBusiness") },
    ],
    [tt],
  );

  const developmentStageOptions = useMemo(
    () => [
      { value: "", label: tt("selectStage") },
      { value: "Seed", label: tt("seed") },
      { value: "Early", label: tt("earlyStage") },
      { value: "Growth", label: tt("growth") },
      { value: "Expansion", label: tt("expansion") },
    ],
    [tt],
  );

  const presentationPreferences = useMemo(
    () => [
      { name: "isPresentationFormatWrittedReports", label: tt("writtenReports") },
      { name: "isPresentationFormatVideoPitches", label: tt("videoPitches") },
      { name: "isPresentationFormatInPersonPitches", label: tt("inPersonPitches") },
    ],
    [tt],
  );

  const investmentTimeOptions = useMemo(
    () => [
      { name: "isInvestmentTimeShortTerm", label: tt("shortTerm") },
      { name: "isInvestmentTimeMediumTerm", label: tt("mediumTerm") },
      { name: "isInvestmentTimeLongTerm", label: tt("longTerm") },
    ],
    [tt],
  );

  const investmentSizes = useMemo(
    () =>
      INVESTMENT_SIZE_VALUES.map((value, i) => ({
        value,
        label: tt(INVESTMENT_SIZE_KEYS[i] as InvestorFormUiKey),
      })),
    [tt],
  );

  const previousInvestmentsOptions = useMemo(
    () => [
      { value: "", label: tt("selectPreviousInvestments") },
      { value: "0-5", label: tt("prevInvest0to5") },
      { value: "6-10", label: tt("prevInvest6to10") },
      { value: "11-50", label: tt("prevInvest11to50") },
      { value: "51-100", label: tt("prevInvest51to100") },
      { value: "100+", label: tt("prevInvest100plus") },
    ],
    [tt],
  );

  const sectorLabelByValue = useCallback(
    (value: string) =>
      sectorOptions.find((o) => o.value === value)?.label ?? value,
    [sectorOptions],
  );

  const yesNoLabel = useCallback(
    (value: string | undefined) => {
      if (value === "1") return tt("yes");
      if (value === "0") return tt("no");
      return tt("selectOption");
    },
    [tt],
  );

  return {
    tt,
    sectorOptions,
    yesNoOptions,
    investmentTypeOptions,
    developmentStageOptions,
    presentationPreferences,
    investmentTimeOptions,
    investmentSizes,
    previousInvestmentsOptions,
    sectorLabelByValue,
    yesNoLabel,
  };
}

export function useCompanyFormLabels() {
  const { i18n } = useTranslation();
  const uiText = useMemo(() => ({ ...COMPANY_FORM_UI_TEXT }), []);
  const { data: translatedUiText } = useTranslateDynamicFields(
    uiText,
    i18n.resolvedLanguage || i18n.language,
  );
  const tt = useCallback(
    (key: CompanyFormUiKey) => translatedUiText?.[key] ?? uiText[key],
    [translatedUiText, uiText],
  );

  const sectorUiText = useMemo(
    () =>
      Object.fromEntries(
        SORTED_SECTORS.map((s, i) => [`sector_${i}`, s]),
      ),
    [],
  );
  const { data: translatedSectors } = useTranslateDynamicFields(
    sectorUiText,
    i18n.resolvedLanguage || i18n.language,
  );
  const sectorOptions = useMemo(
    () =>
      SORTED_SECTORS.map((s, i) => ({
        value: s,
        label: translatedSectors?.[`sector_${i}`] ?? s,
      })),
    [translatedSectors],
  );

  const developmentStageOptions = useMemo(
    () => [
      { value: "", label: tt("selectStage") },
      { value: "Seed", label: tt("seed") },
      { value: "Early", label: tt("earlyStage") },
      { value: "Growth", label: tt("growth") },
      { value: "Expansion", label: tt("expansion") },
    ],
    [tt],
  );

  const countryUiText = useMemo(
    () =>
      Object.fromEntries(
        COUNTRY_NAMES.map((name, i) => [`country_${i}`, name]),
      ),
    [],
  );
  const { data: translatedCountries } = useTranslateDynamicFields(
    countryUiText,
    i18n.resolvedLanguage || i18n.language,
  );
  const countryOptions = useMemo(
    () =>
      COUNTRY_NAMES.map((name, i) => ({
        value: name,
        label: translatedCountries?.[`country_${i}`] ?? name,
      })),
    [translatedCountries],
  );

  const sectorLabelByValue = useCallback(
    (value: string) =>
      sectorOptions.find((o) => o.value === value)?.label ?? value,
    [sectorOptions],
  );

  return {
    tt,
    sectorOptions,
    developmentStageOptions,
    countryOptions,
    sectorLabelByValue,
  };
}

export function useProfileShellLabels() {
  const { i18n } = useTranslation();
  const uiText = useMemo(() => ({ ...PROFILE_SHELL_UI_TEXT }), []);
  const { data: translatedUiText } = useTranslateDynamicFields(
    uiText,
    i18n.resolvedLanguage || i18n.language,
  );
  const tt = useCallback(
    (key: ProfileShellUiKey) => translatedUiText?.[key] ?? uiText[key],
    [translatedUiText, uiText],
  );
  return { tt };
}
